import { AuthContext } from "../Context/AuthContext";
import { toast } from "react-toastify";
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  Paper,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Fade,
  Zoom
} from "@mui/material";
import { useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import CountryPhoneInput from "../components/CountryPhoneInput";
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaCheckCircle, 
  FaUserPlus,
  FaEye,
  FaEyeSlash
} from "react-icons/fa";

function GetDetails() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setIsAuthenticated, setUser, setToken } = useContext(AuthContext);

  const [params] = useSearchParams();
  const { state } = useLocation();
  const navigateTo = useNavigate();

  useEffect(() => {
    if (!state) return;
    if (state.firstName) setFirstName(state?.firstName || "");
    if (state.lastName) setLastName(state?.lastName || "");
    if (state.email) setEmail(state?.email || "");
  }, [state]);

  // Determine if SSO (Google/Apple) flow
  const isSSO = !!state?.email && (state?.appleSub || state?.ssoEnabled || state?.fromSSO);

  useEffect(() => {
    const emailParam = params.get("email") === "true";
    const appleSub = !!params.get("appleSub");
    if (emailParam && !appleSub && !isSSO) {
      navigateTo("/login");
    }
  }, [params, navigateTo, state, isSSO]);

  async function handleRegistration(e) {
    e.preventDefault();
    setLoading(true);
    try {
      // For SSO, always use state.email, but fallback to email state if missing
      let emailToUse = email;
      if (isSSO) {
        emailToUse = state?.email || email;
      } else if (params.get("email") === "true") {
        emailToUse = email;
      } else if (state?.email) {
        emailToUse = state.email;
      }
      // Debug logging
      console.log("Debug - Registration form data:", {
        firstName,
        lastName,
        phone,
        email: emailToUse,
        paramsEmail: params.get("email"),
        stateEmail: state?.email,
        appleSub: params.get("appleSub"),
        password: password ? "***" : "none",
        isSSO
      });
      // Validate other required fields
      if (!firstName) {
        toast.error("First name is required!");
        return;
      }
      if (!lastName) {
        toast.error("Last name is required!");
        return;
      }
      if (!phone) {
        toast.error("Phone number is required!");
        return;
      }
      // Only validate email and password for non-SSO flows
      if (!isSSO) {
        if (!emailToUse) {
          toast.error("Email is required!");
          return;
        }
        if (params.get("password") === "true" && !password) {
          toast.error("Password is required!");
          return;
        }
        if (password && password.length < 8) {
          toast.error("Password must be at least 8 characters long!");
          return;
        }
      }
      console.log("[Patient Registration] Phone with country code:", phone);
      
      let api_body = {
        firstName,
        lastName,
        phone,
        organization: import.meta.env.VITE_TENANT_OVERRIDE || 'vikramaura-hospitals', // Send tenant organization
      };
      
      let endpoint = "";
      if (isSSO) {
        // Use onboarding endpoint for SSO flows
        api_body.appleSub = params.get("appleSub");
        endpoint = "/complete-onboarding";
      } else {
        // Use regular registration endpoint for non-SSO flows
        api_body.email = emailToUse;
        endpoint = "/user/patient/register";
        if (params.get("password") === "true") {
          api_body.password = password;
        }
      }
      
      const [data, axiosError] = await axios
        .post(import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION + endpoint + "?token=true", api_body, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        })
        .then(({ data }) => [data, undefined])
        .catch((err) => [undefined, err]);
      if (axiosError) {
        const errMsg = axiosError.response?.data?.message?.toLowerCase() || "";
        // If error is about OTP/verification, redirect to verify-otp
        if (errMsg.includes("verify your email") || errMsg.includes("otp")) {
          // Only redirect to OTP if not SSO
          if (!isSSO) {
            navigateTo("/verify-otp", { state: { email: emailToUse } });
          }
          return;
        }
        toast.error(axiosError.response.data.message);
        return;
      }
      // Debug: log backend response
      console.log("Registration response:", data);
      // If OTP required, show OTP input and redirect to verify-otp page
      if (
        (data.message && data.message.toLowerCase().includes("verify your email")) ||
        (data.success === false && data.message && data.message.toLowerCase().includes("otp"))
      ) {
        // Only redirect to OTP if not SSO
        if (!isSSO) {
          navigateTo("/verify-otp", { state: { email: emailToUse } });
        }
        return;
      }
      // Success - User should be active, proceed to login
      toast.success(data.message);
      setIsAuthenticated(true);
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.expiresAt) {
        localStorage.setItem('tokenExpiresAt', data.expiresAt);
      }
      navigateTo("/");
      setPassword("");
      setFirstName("");
      setLastName("");
      setPhone("");
    } catch (err) {
      console.error(err);
      toast.error("Some Error Occured");
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    try {
      const [data, axiosError] = await axios
        .post(import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION + "/user/verify-otp", {
          email: pendingEmail,
          otp,
        })
        .then(({ data }) => [data, undefined])
        .catch((err) => [undefined, err]);
      if (axiosError) {
        toast.error(axiosError.response.data.message);
        return;
      }
      toast.success("Email verified! You can now log in.");
      setShowOtp(false);
      setOtp("");
      setPendingEmail("");
      navigateTo("/login");
    } catch (err) {
      toast.error("OTP verification failed");
    }
  }

  return (
    <>
      <Navbar />
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f1f1f1 0%, #f1f0f3 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2
        }}
      >
        <Fade in timeout={800}>
          <Paper 
            elevation={24}
            sx={{
              maxWidth: 600,
              width: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {/* Header Section */}
            <Box 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                py: 4,
                px: 3,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                }
              }}
            >
              <Zoom in timeout={600}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      mb: 2,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <FaUserPlus size={40} />
                  </Box>
                  <Typography variant="h4" fontWeight="700" gutterBottom>
                    {showOtp ? 'Verify Your Account' : 'Create Account'}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {showOtp 
                      ? 'Enter the verification code sent to your email'
                      : isSSO 
                        ? 'Complete your profile to get started'
                        : 'Join us today and experience excellence'
                    }
                  </Typography>
                </Box>
              </Zoom>
            </Box>

            {loading && <LinearProgress />}

            {/* Form Section */}
            <Box sx={{ p: 4 }}>
              {!showOtp ? (
                <form onSubmit={handleRegistration}>
                  <Grid container spacing={2.5}>
                    {/* Name Fields */}
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="First Name" 
                        variant="outlined" 
                        value={firstName} 
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaUser color="#667eea" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#667eea',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="Last Name" 
                        variant="outlined" 
                        value={lastName} 
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaUser color="#667eea" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#667eea',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* Phone Field */}
                    <Grid item xs={12}>
                      <CountryPhoneInput
                        value={phone}
                        onChange={setPhone}
                        required
                        disabled={loading}
                        label="Mobile Number"
                      />
                    </Grid>

                    {/* Email Field - Only for non-SSO */}
                    {!isSSO && params.get("email") === "true" && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          label="Email Address" 
                          type="email" 
                          variant="outlined" 
                          value={email} 
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FaEnvelope color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      </Grid>
                    )}

                    {/* Password Field - Only for non-SSO */}
                    {!isSSO && params.get("password") === "true" && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          label="Password" 
                          type={showPassword ? "text" : "password"}
                          variant="outlined" 
                          value={password} 
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={loading}
                          helperText="Minimum 8 characters required"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FaLock color="#667eea" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  disabled={loading}
                                >
                                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      </Grid>
                    )}

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        fullWidth
                        disabled={loading}
                        sx={{
                          py: 1.8,
                          mt: 2,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                          }
                        }}
                      >
                        {loading ? 'Creating Account...' : 'Continue'}
                      </Button>
                    </Grid>

                    {/* Info Box */}
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                          border: '1px solid rgba(102, 126, 234, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <FaCheckCircle color="#667eea" />
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            Secure Registration
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Your information is encrypted and protected. We'll never share your data without permission.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <form onSubmit={handleOtpSubmit}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      Verification Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We've sent a 6-digit code to
                    </Typography>
                    <Chip 
                      label={pendingEmail} 
                      sx={{ 
                        mt: 1,
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      }}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Enter OTP"
                    variant="outlined"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    inputProps={{ 
                      style: { fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' },
                      maxLength: 6
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />

                  <Button 
                    type="submit" 
                    variant="contained" 
                    fullWidth
                    sx={{
                      py: 1.8,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      }
                    }}
                  >
                    Verify & Continue
                  </Button>
                </form>
              )}
            </Box>
          </Paper>
        </Fade>
      </Box>
    </>
  );
}

export default GetDetails;
